import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, MessageSquareText, X } from "lucide-react";
import { SectionCard } from "@/components/common/SectionCard";
import { DataTable } from "@/components/common/DataTable";
import { EntryModal } from "@/components/common/EntryModal";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { StatusBadge } from "@/components/common/StatusBadge";
import { RowActions } from "./RowActions";
import { Button } from "@/components/ui/button";
import { useCrud } from "@/hooks/useCrud";
import { useUser } from "@/context/UserContext";
import { OWNERS, AREAS } from "@/data/commandOptions";

const fmt = (iso) => {
  try { return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
};

const FIELDS = [
  { name: "title", label: "Update title", type: "text", placeholder: "e.g. Content team hit milestone", required: true },
  { name: "body", label: "Update text", type: "textarea", placeholder: "What's the latest?", required: true },
  { name: "area", label: "Area", type: "select", placeholder: "Area", options: AREAS },
  { name: "related_type", label: "Related record type", type: "text", placeholder: "e.g. Priority, Blocker" },
  { name: "related_name", label: "Related record name", type: "text", placeholder: "e.g. Onboarding flow" },
];

export const UpdatesPanel = ({ filter }) => {
  const { items, save, remove } = useCrud("updates", "Update");
  const { currentUser, canWrite } = useUser();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const weekOnly = filter === "week";
  let rows = items;
  if (weekOnly) {
    const cutoff = new Date(Date.now() - 7 * 864e5).toISOString();
    rows = items.filter((u) => u.created_at && u.created_at >= cutoff);
  }

  const openAdd = () => { setEditing({ author: currentUser.name, area: "General" }); setModalOpen(true); };
  const openEdit = (row) => { setEditing(row); setModalOpen(true); };
  const handleSave = (values) => save({ ...values, author: values.author || currentUser.name });

  const columns = [
    { key: "title", header: "Update", render: (r) => (
      <div className="max-w-md">
        <p className="font-medium text-[#0D1B2A]">{r.title}</p>
        <p className="mt-0.5 line-clamp-2 text-xs text-[#5C6B7B]">{r.body}</p>
        {r.related_name && <p className="mt-1 text-[11px] text-[#5C6B7B]">↳ {r.related_type}: {r.related_name}</p>}
      </div>
    )},
    { key: "author", header: "Author", render: (r) => <StatusBadge status="info" label={r.author} /> },
    { key: "area", header: "Area" },
    { key: "created_at", header: "Posted", render: (r) => <span className="text-xs text-[#5C6B7B]">{fmt(r.created_at)}</span> },
    { key: "actions", header: "", thClassName: "text-right", className: "text-right", render: (r) => (
      <RowActions idx={r.id} onEdit={() => openEdit(r)} onDelete={() => setConfirmId(r.id)} />
    )},
  ];

  return (
    <SectionCard
      testid="updates-card"
      accent="#0EA5A4"
      eyebrow="Expedition Log"
      icon={MessageSquareText}
      title="Company Updates"
      subtitle="A running log of written updates, newest first."
      action={canWrite ? <Button size="sm" data-testid="add-update-btn" onClick={openAdd} className="rounded-full bg-[#0EA5A4] text-white hover:bg-[#0C8E8D]"><Plus size={15} /> Post Update</Button> : null}
    >
      {weekOnly && (
        <div data-testid="updates-filter-banner" className="mb-3 flex items-center justify-between rounded-xl bg-[#0EA5A4]/10 px-3 py-2 text-xs font-semibold text-[#0C8E8D]">
          <span>Filtered to updates from the last 7 days</span>
          <button data-testid="clear-filter" onClick={() => navigate("/?tab=updates")} className="inline-flex items-center gap-1 underline hover:no-underline"><X size={13} /> Clear</button>
        </div>
      )}
      <DataTable
        testid="updates-table"
        columns={columns}
        rows={rows}
        searchKeys={["title", "body", "author"]}
        searchPlaceholder="Search updates…"
        filters={[
          { key: "author", label: "Author", options: OWNERS },
          { key: "area", label: "Area", options: AREAS },
        ]}
        emptyIcon={MessageSquareText}
        emptyTitle="No updates yet"
        emptyDescription="Share the first written update to start a timeline of progress."
        emptyActionLabel={canWrite ? "Post update" : undefined}
        onEmptyAction={canWrite ? openAdd : undefined}
      />
      <EntryModal open={modalOpen} onOpenChange={setModalOpen}
        title={editing?.id ? "Edit Update" : "Post an Update"}
        description={`Posting as ${currentUser.name}.`}
        submitLabel={editing?.id ? "Save Changes" : "Post Update"}
        fields={FIELDS} initialValues={editing} onSubmit={handleSave} />
      <ConfirmDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}
        title="Delete this update?" description="This update will be permanently removed."
        onConfirm={() => { remove(confirmId); setConfirmId(null); }} />
    </SectionCard>
  );
};
